import { useState } from "react";
import { Alert, Badge, Button, CloseButton, Group, Stack, Text } from "@mantine/core";
import { IconAlertTriangle, IconBell, IconCalendar, IconClipboardList } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useProjectAlerts, type ProjectAlert, type AlertSeverity } from "@/hooks/useProjectAlerts";

interface AlertBannerProps {
  projectId: string;
}

const ALERT_ICONS: Record<string, typeof IconBell> = {
  BUDGET_OVER: IconAlertTriangle,
  COMMITMENT_GAP: IconAlertTriangle,
  PENDING_LOGS: IconClipboardList,
  EXPIRING_COMMITMENT: IconCalendar,
};

const SEVERITY_COLORS: Record<AlertSeverity, string> = {
  critical: "red",
  warning: "yellow",
  info: "blue",
};

/**
 * PM Alert Banner component
 *
 * Displays important project alerts at the top of the project page:
 * - Budget alerts (cost codes over budget)
 * - Pending daily logs to review
 * - Upcoming inspections/deadlines
 * - Commitment gaps
 *
 * The banner is dismissible for the session.
 */
export function AlertBanner({ projectId }: AlertBannerProps) {
  const { data, isLoading, error } = useProjectAlerts(projectId);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  // Don't render if dismissed or loading
  if (dismissed || isLoading) {
    return null;
  }

  // Don't render if error or no alerts
  if (error || !data || data.totalCount === 0) {
    return null;
  }

  const handleAlertClick = (alert: ProjectAlert) => {
    if (alert.actionUrl) {
      navigate({ to: alert.actionUrl as any });
    }
  };

  const handleReviewAll = () => {
    // Navigate to cost dashboard as the primary alert review page
    navigate({ to: `/projects/${projectId}/cost-dashboard` as any });
  };

  const { alerts, criticalCount, warningCount } = data;

  // Determine overall banner color based on highest severity
  const bannerColor = criticalCount > 0 ? "red" : warningCount > 0 ? "yellow" : "blue";

  // Limit display to top 5 alerts
  const displayAlerts = alerts.slice(0, 5);
  const hasMoreAlerts = alerts.length > 5;

  return (
    <Alert
      variant="light"
      color={bannerColor}
      title={
        <Group gap="sm" wrap="nowrap">
          <IconBell size={20} />
          <Text fw={600} size="sm">
            TODAY'S ALERTS
          </Text>
          {criticalCount > 0 && (
            <Badge color="red" variant="filled" size="sm">
              {criticalCount} Critical
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge color="yellow" variant="filled" size="sm">
              {warningCount} Warning
            </Badge>
          )}
        </Group>
      }
      withCloseButton={false}
      style={{ position: "relative" }}
    >
      <CloseButton
        onClick={() => setDismissed(true)}
        style={{ position: "absolute", top: "12px", right: "12px" }}
        aria-label="Dismiss alerts"
      />

      <Stack gap="xs" mt="xs">
        {displayAlerts.map((alert) => {
          const Icon = ALERT_ICONS[alert.type] || IconBell;
          const severityColor = SEVERITY_COLORS[alert.severity];

          return (
            <Group
              key={alert.id}
              gap="sm"
              wrap="nowrap"
              style={{
                cursor: alert.actionUrl ? "pointer" : "default",
                padding: "4px 0",
              }}
              onClick={() => handleAlertClick(alert)}
            >
              <Icon size={16} color={`var(--mantine-color-${severityColor}-6)`} />
              <Text size="sm" style={{ flex: 1 }}>
                {alert.title}
              </Text>
            </Group>
          );
        })}

        {hasMoreAlerts && (
          <Text size="xs" c="dimmed" mt="xs">
            +{alerts.length - 5} more alert{alerts.length - 5 === 1 ? "" : "s"}
          </Text>
        )}

        <Group justify="flex-end" mt="sm">
          <Button
            variant="light"
            size="xs"
            rightSection={<Text size="xs">→</Text>}
            onClick={handleReviewAll}
          >
            Review All
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}
